import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Alert,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { Visibility, VisibilityOff, Lock } from "@mui/icons-material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useSnackbar } from "notistack";
import authService from "../../../services/auth/authService";

const validationSchema = Yup.object({
  newPassword: Yup.string()
    .min(8, "Password must be at least 8 characters")
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/,
      "Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character"
    )
    .required("New password is required"),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref("newPassword"), null], "Passwords must match")
    .required("Please confirm your password"),
});

const ChangePasswordModal = ({
  open,
  onClose,
  onSuccess,
  userEmail,
  isFirstLogin = false,
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const formik = useFormik({
    initialValues: {
      newPassword: "",
      confirmPassword: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      setLoading(true);
      try {
        const response = await authService.firstLoginPasswordChange({
          newPassword: values.newPassword,
          confirmPassword: values.confirmPassword,
        });

        if (response.success) {
          enqueueSnackbar(
            "Password changed successfully! You can now access the system.",
            { variant: "success" }
          );
          formik.resetForm();
          onSuccess();
        } else {
          enqueueSnackbar(response.message || "Failed to change password", {
            variant: "error",
          });
        }
      } catch (error) {
        console.error("Password change error:", error);
        const errorMessage =
          error.response?.data?.message ||
          error.message ||
          "Failed to change password";
        enqueueSnackbar(errorMessage, { variant: "error" });
      } finally {
        setLoading(false);
      }
    },
  });

  const handleClose = (event, reason) => {
    // Prevent closing if it's a first login (mandatory password change)
    if (isFirstLogin) {
      return;
    }

    // Only allow closing if not loading
    if (!loading) {
      formik.resetForm();
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      fullWidth
      onClose={handleClose}
      disableEscapeKeyDown={true}
      // Make backdrop non-interactive and darker for first login
      BackdropProps={{
        style: {
          backgroundColor: isFirstLogin
            ? "rgba(0, 0, 0, 0.8)"
            : "rgba(0, 0, 0, 0.5)",
          pointerEvents: isFirstLogin ? "none" : "auto",
        },
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <Lock color="primary" />
          <Typography variant="h6">Change Your Password</Typography>
        </Box>
      </DialogTitle>

      <form onSubmit={formik.handleSubmit}>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 3 }}>
            <strong>REQUIRED:</strong> You must change your password before
            accessing the system. This dialog cannot be closed until you set a
            new password.
          </Alert>

          {userEmail && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Account: <strong>{userEmail}</strong>
            </Typography>
          )}

          <TextField
            fullWidth
            name="newPassword"
            label="New Password"
            type={showPassword ? "text" : "password"}
            value={formik.values.newPassword}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={
              formik.touched.newPassword && Boolean(formik.errors.newPassword)
            }
            helperText={formik.touched.newPassword && formik.errors.newPassword}
            margin="normal"
            disabled={loading}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                    disabled={loading}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <TextField
            fullWidth
            name="confirmPassword"
            label="Confirm New Password"
            type={showConfirmPassword ? "text" : "password"}
            value={formik.values.confirmPassword}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={
              formik.touched.confirmPassword &&
              Boolean(formik.errors.confirmPassword)
            }
            helperText={
              formik.touched.confirmPassword && formik.errors.confirmPassword
            }
            margin="normal"
            disabled={loading}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    edge="end"
                    disabled={loading}
                  >
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 2, display: "block" }}
          >
            Password requirements:
            <br />• At least 8 characters long
            <br />• Contains uppercase and lowercase letters
            <br />• Contains at least one number
            <br />• Contains at least one special character (@$!%*?&)
          </Typography>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button
            type="submit"
            variant="contained"
            disabled={loading || !formik.isValid || !formik.dirty}
            fullWidth
            size="large"
          >
            {loading ? "Changing Password..." : "Change Password"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ChangePasswordModal;
